import * as Utils from '../utils/common-utils'
import { CSVProfile, CSVTranformationProfile, IChartData, IDataPoint } from '../../types'
import { TRANSFORM_EXCLUDE_KEY, TRANSFORM_MILLI_TO_DATE } from '../commons/constants'
import { BaseVisualization } from '../base/base-visualization-component'
import { LegendClickHandlers } from '@meltwater/mw-visualizations-angularjs'
import { MwChartNSeriesLineOptions } from '@meltwater/mw-visualizations-angularjs'
import cloneDeep from 'lodash/cloneDeep'
import dropRight from 'lodash/dropRight'
import forEach from 'lodash/forEach'
import isEmpty from 'lodash/isEmpty'
import isUndefined from 'lodash/isUndefined'
import momentTz from 'moment-timezone'
import { prettyNames } from '../../charts-factory'

export class LineChartComponent extends BaseVisualization {
 static config = {
  selector: 'visualizationWidget',
  controller: LineChartComponent,
  template: `
          <mw-chart-n-series-line
            ng-if="!$ctrl.isLoading && !$ctrl.isObjEmpty($ctrl.visualizationDataArray)"
            chart-data="$ctrl.visualizationDataArray"
            chart-options="$ctrl.visualizationOptions"
            get-api="$ctrl.getApi(API)">
          </mw-chart-n-series-line>
`,
  bindings: BaseVisualization.config.bindings,
 }

 public granularity: string
 public hideLegend = false
 public onLegendClick = LegendClickHandlers.HIDE_SERIES
 public multiSource = true
 public seriesColors
 public removeAll = true
 public isCompare = false
 private lastDate = null

 // default CSV header columns. Can be overriden in sub class.
 public csvHeaders = ['Date', 'Count']
 public transformationProfiles: CSVTranformationProfile[] = [
  {
   key: 'key',
   type: TRANSFORM_MILLI_TO_DATE,
  },
  {
   key: 'code',
   type: TRANSFORM_EXCLUDE_KEY,
  },
 ]

 $onChanges(changes) {
  if (this.hasChanges(changes.visualizationMetaData)) {
   this.setMetaData(changes.visualizationMetaData.currentValue)
  }
  if (changes.vizData && typeof changes.vizData.currentValue !== 'undefined') {
   if (!this.isObjEmpty(changes.vizData.currentValue)) {
    this.granularity = isUndefined(changes.vizData.currentValue.originalViewRequest)
     ? 'DAY'
     : changes.vizData.currentValue.originalViewRequest.granularity
    this.setProperties()
    this.updateData(changes.vizData.currentValue)
    this.updateOptions()
    this.buildCSVExportProfile()
   } else {
    this.visualizationDataArray = []
   }
  }
 }

 setProperties() {}

 updateData(data) {
  if (this.isCompare) {
   data.data = this.sortCompareObject(data.data, this.visualizationMetaData)
  }
  const limit = 10
  this.visualizationDataArray = []

  if (!isUndefined(data.data)) {
   this.visualizationDataArray = Object.keys(data.data).map(key => {
    let total = 0
    let value = []

    if (!isUndefined(data.data[key])) {
     data.data[key].map(x => {
      total = total + x.value
      value.push(
       this.returnDataPoint(
        parseInt(x.key),
        x.key,
        x.value || 0,
        isEmpty(x.code) ? '' : x.code,
        prettyNames(key)
       )
      )
     })
    }
    return this.returnDataObj(key, total, value)
   })
  }

  if (this.visualizationDataArray.length >= limit && !this.isCompare) {
   this.visualizationDataArray = this.addOtherDataLine(this.visualizationDataArray, limit)
  } else if (this.visualizationDataArray.length === 2 && this.removeAll && !this.isCompare) {
    this.visualizationDataArray = [this.visualizationDataArray.find(x =>  x.code !== 'All')]
  }
  this.setProperties()
 }

 updateOptions() {
  this.visualizationOptions = new MwChartNSeriesLineOptions(this.buildLatestActivityChartOptions())
 }

 onPointClick = ({ keyIndex }) => {
  this.triggerUpdate(
   this.updatePayloadDate(this.vizData.data.all[keyIndex].key, this.vizData.data.all[keyIndex].key)
  )
 }

 lineDateClick = obj => {
  let payload = []
  payload.push(this.updatePayloadDate(obj.key, obj.key))
  this.triggerUpdate(payload)
 }

 lineDateSentimentClick = obj => {
  const seriesName = obj.seriesName.split(' <br />')[0]
  let payload = []
  payload.push(
   this.updatePayload(
    'FILTER_CHANGE',
    'append',
    'sentiment',
    this.namingUtils.getSentimentType(seriesName)
   )
  )
  payload.push(this.updatePayloadDate(obj.key, obj.key))

  this.triggerUpdate(payload)
 }

 tooltipFormatter = point => {
  if (!this.multiSource) {
   return (
    '<strong>Date: </strong>' +
    momentTz(point.key).tz(this.properties.userTimeZone).format('MM-DD-YYYY') +
    '<br><strong>Time: </strong>' +
    momentTz(point.key).tz(this.properties.userTimeZone).format('hh:mm A') +
    '<br><strong>Count: </strong>' +
    point.value
   )
  } else {
   const sourceTag = this.tagSourceFormat(point)

   //ALPHA-673
   if (point.seriesName.split('<br />')[0] === 'Other ') {
    return '' + momentTz(point.key).format('MMM DD') + '<br>' + sourceTag
   }

   return (
    '<strong>Source: </strong>' +
    sourceTag +
    '<br><strong>Date: </strong>' +
    momentTz(point.key).tz(this.properties.userTimeZone).format('MM-DD-YYYY') +
    '<br><strong>Time: </strong>' +
    momentTz(point.key).tz(this.properties.userTimeZone).format('hh:mm A') +
    '<br><strong>Count: </strong>' +
    point.value
   )
  }
 }

 xAxisLabelFormatter = axisData => {
  const arrayNumber = this.visualizationDataArray.findIndex(element => element.data.length > 0)
  const startDate = Number(this.visualizationDataArray[arrayNumber].data[0].key)
  const endDate = Number(
   this.visualizationDataArray[arrayNumber].data[this.visualizationDataArray[arrayNumber].data.length - 1].key
  )
  const timeDiff = (endDate - startDate) / 1000 / 3600

  if (timeDiff > 48) {
   this.granularity = 'WEEK'
  } else if (timeDiff > 23) {
   this.granularity = 'DAY'
  } else if (timeDiff > 1) {
   this.granularity = 'HOUR'
  } else if (timeDiff > 0) {
   this.granularity = 'MINUTE'
  } else {
   //do nothing
  }
  return this.dateFormat(this.granularity, axisData, this.properties.userTimeZone)
 }

 tagSourceFormat(point) {
  if (point.seriesName.split('<br />')[0] === 'Other ') {
   //ALPHA-673
   let otherSource = this.visualizationDataArray
    .find(x => point.seriesName === x.name)
    .data.find(y => y.key === point.key).name

   let otherSourceList = otherSource.split('<br />')

   let formattedSource = ''
   otherSourceList.forEach(element => {
    formattedSource +=
     '<span style="height: 8px; width: 8px; background-color: #000; border-radius: 50%; display: inline-block;"></span>  ' +
     element.trim() +
     '<br />'
   })

   return formattedSource
  } else {
   return point.seriesName.split('<br />')[0]
  }
 }

 buildLatestActivityChartOptions() {
  return new MwChartNSeriesLineOptions({
   hideLegend: this.hideLegend,
   onLegendClick: this.onLegendClick,
   onPointClick: this.onPointClick,
   tooltipFormatter: this.tooltipFormatter,
   xAxisLabelFormatter: this.xAxisLabelFormatter,
   seriesColors: this.seriesColors,
  })
 }

 setSentimentColorPalette = data => {
  let palette = []
  data.map(x => {
   const key = isUndefined(x.key) ? x.code : x.key
   switch (key) {
    case 'Positive':
     palette.push('#75B000')
     break
    case 'Neutral':
     palette.push('#9E9E9E')
     break
    case 'Negative':
     palette.push('#D0021D')
     break
    case 'Not Rated':
     palette.push('#EEEEEE')
     break
    default:
     palette.push('#EEEEEE')
     break
   }
  })
  return palette
 }

 setTweetColorPalette(data) {
  let palette = []
  data.map(x => {
   const key = isUndefined(x.key) ? x.code : x.key
   switch (key) {
    case 'All':
     palette.push('#3399FF')
     break
    case 'Replies':
     palette.push('#18D19F')
     break
    case 'Retweets':
     palette.push('#FFBC43')
     break
    case 'Original Tweets':
     palette.push('#F65C8A')
     break
    case 'Quoted':
     palette.push('#FF743F')
     break
    default:
     palette.push('#3399FF')
     break
   }
  })
  return palette
 }

 getSentimentType(SentimentType: string) {
  switch (SentimentType) {
   case 'Positive':
    return 'p'
    break
   case 'Neutral':
    return 'n'
    break
   case 'Negative':
    return 'v'
    break
   case 'Not Rated':
    return 'u'
    break
   default:
    break
  }
 }

 returnDataObj(key: string, total: number, value: Array<IDataPoint>, code?: string): IChartData {
  return {
   name: `${prettyNames(key)} <br /> ${this.numberTransform(total)}`,
   code: prettyNames(key),
   data: value,
  }
 }

 returnDataPoint(
  key: string | number,
  timestamp: string,
  value: string | number,
  code: string,
  name?: string
 ): IDataPoint {
  return { key, timestamp, value, code, name }
 }

 addOtherDataLine(data: Array<IChartData>, arrayLength: number) {
  let merge = []
  let code = []
  let total = []
  let dataSlice = cloneDeep(data.slice(arrayLength - 2))

  forEach(dataSlice, x => {
   const totalPush = x.data.map(i => i.value)
   total = total.concat(totalPush)
   code.push(x.data[0].code)
   merge = isEmpty(merge) ? this.nameUpdate(x) : this.mergeData(merge, x)
  })

  data = dropRight(data, dataSlice.length)
  data.push(this.returnDataObj('Other', total.reduce((a, b) => a + b), merge, code.join(',')))

  return data
 }

 nameUpdate(data: IChartData) {
  return data.data.map(point => {
   point.name = `${prettyNames(data.code)} ${point.value}`
   return point
  })
 }
 mergeData(objOne: IDataPoint[], objTwo: IChartData) {
  return objOne.map(x => {
   let match = objTwo.data.find(y => y.key === x.key)
   return this.returnDataPoint(
    x.key,
    x.timestamp,
    Number(x.value) + Number(match.value),
    `${x.code}, ${prettyNames(match.code)}`,
    `${x.name} <br /> ${prettyNames(objTwo.code)} ${Number(match.value)}`
   )
  })
 }

 nameFormat(point) {
  if (point.seriesName.split('<br />')[0] === 'Other ') {
   return `<br />${this.findDataPoint(point.seriesName, point.keyIndex).code}<br />`
  } else {
   return point.seriesName.split('<br />')[0]
  }
 }

 getSourcedata(seriesName) {
  return this.visualizationDataArray.find(x => x.name === seriesName)
 }

 findDataPoint(seriesName: string, keyIndex: number) {
  return this.visualizationDataArray.find(x => x.name === seriesName).data[keyIndex]
 }

 dateFormat(granularity, axisData, userTimeZone) {
  let date = axisData.value
  if (granularity === 'HOUR') {
   if (axisData.isFirst) {
    return momentTz(date).tz(userTimeZone).format('MMM D')
   } else {
    return momentTz(date).tz(userTimeZone).format('h:mm A')
   }
  } else if (granularity === 'MINUTE') {
   return momentTz(date).tz(userTimeZone).format('h:mm A')
  } else if (granularity === 'DAY') {
   if (axisData.isFirst) {
    this.lastDate = momentTz(date).tz(userTimeZone).format('MMM D')
    return momentTz(date).tz(userTimeZone).format('MMM D')
   } else {
    if (momentTz(date).tz(userTimeZone).format('MMM D') !== this.lastDate) {
     this.lastDate = momentTz(date).tz(userTimeZone).format('MMM D')
     return momentTz(date).tz(userTimeZone).format('MMM D')
    } else {
     return momentTz(date).tz(userTimeZone).format('h:mm A')
    }
   }
  } else {
   return momentTz(date).tz(userTimeZone).format('MMM D')
  }
 }

 sourceTypeFilter(obj): any[] {
  let seriesData = this.getSourcedata(obj.seriesName)
  let seriesName = seriesData.data[0].code
  if (seriesData.code === 'Other') {
   return seriesName.split(', ').map(x => {
    return this.updatePayload('FILTER_CHANGE', 'append', 'sourceType', x)
   })
  } else {
   return [this.updatePayload('FILTER_CHANGE', 'append', 'sourceType', seriesName)]
  }
 }

 buildCSVExportProfile() {
  this.csvExportProfiles = []

  let exportData: CSVProfile = {
   data: Object.assign({}, this.vizData.data),
   csvOptions: { title: this.visTitle, headers: this.csvHeaders },
   transformationProfiles: this.transformationProfiles,
  }

  this.csvExportProfiles.push(exportData)
 }
}
